import { useEffect, useRef } from 'react';

/**
 * Google AdSense Component
 * 
 * Usage:
 * <AdSense 
 *   client="ca-pub-5445691431951301"
 *   slot="YOUR_AD_SLOT_ID"  // Get this from AdSense dashboard
 *   format="auto"
 *   responsive={true}
 * />
 * 
 * Note: You must create ad units in your AdSense dashboard first to get the slot ID.
 * "auto" ads will not work without proper slot IDs.
 */
export function AdSense({ 
  client = 'ca-pub-5445691431951301',
  slot,
  format = 'auto',
  responsive = true,
  layout = null, // 'in-article' for fluid in-article ads
  style = {},
  className = ''
}) {
  const adRef = useRef(null);

  useEffect(() => {
    // Only run if we have a valid slot
    if (!slot || slot === 'auto') {
      console.warn('[AdSense] Missing or invalid ad slot. Create ad units in AdSense dashboard.');
      return;
    }

    // Guard against duplicate pushes on the same ad element (e.g., React Strict Mode)
    if (adRef.current && adRef.current.getAttribute('data-ad-loaded') === 'true') {
      return;
    }

    // Push the ad to AdSense
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      adRef.current?.setAttribute('data-ad-loaded', 'true');
    } catch (err) {
      console.error('[AdSense] Error loading ad:', err);
    }
  }, [slot]);

  // Don't render if no valid slot
  if (!slot || slot === 'auto') {
    return (
      <div 
        style={{ 
          padding: '20px', 
          textAlign: 'center', 
          background: '#f8f9fa',
          border: '1px dashed #ddd',
          borderRadius: '8px',
          color: '#666',
          fontSize: '13px',
          ...style 
        }}
        className={className}
      >
        📢 Ad placeholder — Create ad units in AdSense dashboard to display ads
      </div>
    );
  }

  const adProps = {
    'data-ad-client': client,
    'data-ad-slot': slot,
    'data-ad-format': format,
  };

  // Add responsive attribute only for non-fluid ads
  if (format !== 'fluid') {
    adProps['data-full-width-responsive'] = responsive ? 'true' : 'false';
  }

  // Add layout for in-article ads
  if (layout) {
    adProps['data-ad-layout'] = layout;
  }

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className}`}
      style={{ display: 'block', ...style }}
      {...adProps}
    />
  );
}

/**
 * Auto Ads Script Loader
 * Place this in your main App or layout to enable auto ads
 */
export function AdSenseScript() {
  useEffect(() => {
    // Check if script already exists
    if (document.querySelector('script[data-adsense-script]')) return;

    const script = document.createElement('script');
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5445691431951301';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-adsense-script', 'true');
    
    document.head.appendChild(script);

    return () => {
      // Don't remove script on unmount - it's global
    };
  }, []);

  return null;
}
